//hooks
import React, { useState, useEffect } from "react";
import moment from "moment";
import { closeSnackbar, enqueueSnackbar } from "notistack";
//components and constants
import AnalyticsCards from "../../Components/GoogleAnalytics/AnalyticsCards";
import AnalyticsTable from "../../Components/GoogleAnalytics/AnalyticTable";
import SteplineChart from "../../Components/GoogleAnalytics/StepLineChart";
import StatsCard from "../../Components/GoogleAnalytics/StatsCard";
import DonutChart from "../../Components/GoogleAnalytics/DonutChart";
import AnalyticModal from "../../Components/GoogleAnalytics/AnalyticModal";
import Modal from "../../Components/Kanban/Modal/Modal";
import { googleAnalyticsApi } from "../../DataApi/googleAnalytics/googleAnalytics";
import { DISPLAY_DATE_FORMAT } from "../../utilities/constants";
import { useSession } from "../../authentications/SessionContext";
import { read } from "../../DataApi/read";
//images
import DummyImageGA from "../../Images/DummyImage.svg";

const GoogleAnalytics = () => {
  const { sessionInfo } = useSession();
  const { company_id, context, current_website_id } = sessionInfo;
  const [showContent, setShowContent] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [initialCheckDone, setInitialCheckDone] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [cardsData, setCardsData] = useState({});
  const [referrerStats, setReferrerStats] = useState([]);
  const [sessionStats, setSessionStats] = useState([]);
  const [activeUsersByCity, setActiveUsersByCity] = useState([]);
  const [activeUsersByAudience, setActiveUsersByAudience] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userTypeData, setUserTypeData] = useState([]);
  const [keyEventsData, setKeyEventsData] = useState({
    series: [],
    labels: [],
  });

  const [hasCredentials, setHasCredentials] = useState(false);

  const checkGACredentials = async () => {
    try {
      const response = await read({
        id: current_website_id,
        context,
        company_id,
        model: "website",
      });

      const result = JSON.parse(response)?.result?.[0];
      const credentialsExist =
        result?.ga4_property_id?.trim() && result?.ga4_tracking_key?.trim();

      setHasCredentials(!!credentialsExist);
      return credentialsExist;
    } catch (error) {
      return false;
    }
  };

  // Connect button handler
  const handleConnectClick = async () => {
    if (hasCredentials) {
      enqueueSnackbar("Google Analytics is already connected!", {
        variant: "success",
        autoHideDuration: 3000,
      });
      setShowContent(true);
      fetchAllData();
    } else {
      enqueueSnackbar("Please configure Google Analytics credentials", {
        variant: "warning",
        // action: (
        //   <Button
        //     color="inherit"
        //     size="small"
        //     onClick={() => setIsModalOpen(true)}
        //   >
        //     Configure
        //   </Button>
        // ),
      });
      setIsModalOpen(true);
    }
  };

  // Modal success handler
  const handleModalSuccess = async () => {
    const credentialsExist = await checkGACredentials();

    if (credentialsExist) {
      setShowContent(true);
      fetchAllData();
      enqueueSnackbar("Successfully connected to Google Analytics!", {
        variant: "success",
        autoHideDuration: 3000,
      });
    }
    setIsModalOpen(false);
  };

  // Configure button handler - always open modal
  const handleConfigureClick = () => {
    setIsModalOpen(true);
  };

  useEffect(() => {
    if (current_website_id) {
      checkGACredentials().then((credentialsExist) => {
        if (credentialsExist) {
          setShowContent(true);
          fetchAllData();
        }
        setInitialCheckDone(true);
      });
    }
  }, [current_website_id]);

 useEffect(() => {
   let isMounted = true;

   const interval = setInterval(() => {
     if (isMounted) fetchAllData();
   }, 15 * 60 * 1000); // 15 minutes

   return () => {
     isMounted = false;
     clearInterval(interval);
   };
 }, []);

  const fetchData = async (payload) => {
    try {
      let response = await googleAnalyticsApi(payload);
      response = JSON.parse(response);
      // console.log("response:", response);
      return response;
    } catch (error) {
      console.error("fetchData error:", error);
      return {};
    }
  };

  const fetchAllData = async () => {
    //   if (loading) return;

    try {
      setLoading(true);
      const response = await fetchData({});
      const result = response?.result || {};

      // console.log("API Result:", result);

      // Process basic metrics for table
      setTableData(
        Array.isArray(result.basic_metrics) ? result.basic_metrics : []
      );

      // Process cards data
      const engagementSummary = result.engagement_summary || {};
      setCardsData({
        ...engagementSummary,

        avg_engagement_time_per_user: formatTime(
          Number(engagementSummary?.avg_engagement_time_per_user || 0)
        ),
      });
      const processUserTypeData = (data) => {
        if (!Array.isArray(data)) return [];
        return data.map((item) => ({
          date: moment(item.date, "YYYYMMDD").format(DISPLAY_DATE_FORMAT),
          user_type: item.user_type,
          active_users: Number(item.active_users),
        }));
      };

      // Process other data sets
      setReferrerStats(processReferrerData(result.referrer_summary));
      setSessionStats(processSessionData(result.session_by_medium_source));
      setActiveUsersByCity(
        processLocationData(result.active_users_by_location)
      );
      setActiveUsersByAudience(
        processAudienceData(result.active_users_by_audience)
      );
      setUserTypeData(processUserTypeData(result.user_type_breakdown));
      setKeyEventsData(processKeyEventsData(result.key_events_by_platform));
    } catch (error) {
      console.error("API Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const processKeyEventsData = (data) => {
    if (!Array.isArray(data)) return { series: [], labels: [] };
    const series = data.map((item) => Number(item.event_count || 0));
    const labels = data.map((item) => item.platform || "Unknown");
    return { series, labels };
  };
  // Add data processing functions
  const processReferrerData = (data) => {
    return Array.isArray(data)
      ? data.map((item) => [
          `${item.first_user_source || "none"} / ${
            item.first_user_medium || "none"
          }`,
          Number(item.active_users || 0),
        ])
      : [];
  };

  const processSessionData = (data) => {
    return Array.isArray(data)
      ? data.map((item) => [
          `${item.session_source || "none"} / ${item.session_medium || "none"}`,
          Number(item.sessions || 0),
        ])
      : [];
  };

  const processLocationData = (data) => {
    return Array.isArray(data)
      ? data.map((item) => [
          `${item.region || "none"} / ${item.city || "none"}`,
          Number(item.active_users || 0),
        ])
      : [];
  };

  const processAudienceData = (data) => {
    return Array.isArray(data)
      ? data.map((item) => [
          item.audience_name || "none",
          Number(item.active_users || 0),
        ])
      : [];
  };

const handleRefresh = () => {
  fetchAllData();
};


  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const trackEvent = (eventCategory, eventAction, eventLabel) => {
    if (window.gtag) {
      // Google Analytics tracking
      window.gtag("event", eventAction, {
        event_category: eventCategory,
        event_label: eventLabel,
      });
    }
  };
  const handleExploreMoreClick = () => {
    trackEvent("Button", "Click", "Explore More");
    window.open(
      "https://analytics.google.com/analytics/web/#/p474778417/realtime/overview?params=_u..nav%3Dmaui",
      "_blank",
      "noopener,noreferrer"
    );
  };

  const formatTime = (seconds) => {
    return moment.utc(seconds * 1000).format("m[m] s[s]");
  };

if (!initialCheckDone) {
  return (
    <div
      style={{
        minHeight: "80vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <span className="spinner-border text-warning" role="status" />
    </div>
  );
}

  return (
    <>
      {isModalOpen && (
        <Modal isOpen={isModalOpen} onClose={handleCloseModal}>
          <AnalyticModal
            handleCloseModal={handleCloseModal}
            onSuccess={handleModalSuccess}
          />
        </Modal>
      )}

      <div className="position-relative" style={{ minHeight: "80vh" }}>
        {showContent ? (
          <>
            <div className="d-flex justify-content-end p-3">
              <button
                onClick={handleRefresh}
                style={{
                  backgroundColor: "#FF641B",
                  color: "#fff",
                  border: "none",
                  padding: "8px 16px",
                  borderRadius: "6px",
                  fontSize: "0.9rem",
                  cursor: "pointer",
                  fontFamily: "Inter",
                }}
              >
                <i
                  className="fas fa-sync-alt"
                  style={{ marginRight: "8px" }}
                ></i>
                Refresh
              </button>
            </div>

            <div className="section1 px-3">
              <h4
                style={{
                  fontFamily: "Inter",
                  padding: "12px",
                  fontWeight: "bold",
                }}
              >
                User Behaviour
              </h4>
              <AnalyticsCards isLoading={loading} data={cardsData || []} />
              <div className="mt-4">
                <AnalyticsTable isLoading={loading} data={tableData || []} />
              </div>
              <div className="row g-4 mt-4 px-2">
                <div className="col-xl-6 col-lg-12 col-md-12 col-12">
                  <div className="bg-white p-3">
                    <SteplineChart isLoading={loading} data={userTypeData} />
                  </div>
                </div>
                <div className="col-xl-6 col-lg-12 col-md-12 col-12">
                  <div className="" style={{ height: "100%" }}>
                    <DonutChart
                      isLoading={loading}
                      series={keyEventsData.series}
                      labels={keyEventsData.labels}
                    />
                  </div>
                </div>
              </div>
              <div className="row g-4 mt-4 px-2">
                <div className="col-xl-3 col-lg-12 col-md-12 col-12">
                  <StatsCard
                    title="Active users by First user source / medium"
                    headers={["First User ", "Active Users"]}
                    data={referrerStats}
                    isLoading={loading}
                  />
                </div>
                <div className="col-xl-3 col-lg-12 col-md-12 col-12">
                  <StatsCard
                    title="Sessions by Session source/medium"
                    headers={["Session Source", "Sessions"]}
                    data={sessionStats}
                    isLoading={loading}
                  />
                </div>
                <div className="col-xl-3 col-lg-12 col-md-12 col-12">
                  <StatsCard
                    title="Active User by Location"
                    headers={["Town/City", "Active Users"]}
                    data={activeUsersByCity}
                    isLoading={loading}
                  />
                </div>
                <div className="col-xl-3 col-lg-12 col-md-12 col-12">
                  <StatsCard
                    title="Active Users by Audience Name"
                    headers={["Audience Name", "Active Users"]}
                    data={activeUsersByAudience}
                    isLoading={loading}
                  />
                </div>
              </div>
              <div className="row g-4 mt-4 px-2"></div>
              <div className="d-flex justify-content-end gap-3 mt-3">
                <button
                  onClick={handleExploreMoreClick}
                  style={{
                    backgroundColor: "#FF641B",
                    pointerEvents: "auto",
                    color: "#fff",
                    border: "none",
                    padding: "12px 24px",
                    borderRadius: "8px",
                    fontSize: "1rem",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  Explore More <i className="fas fa-chevron-down"></i>
                </button>

                <button
                  onClick={handleConfigureClick}
                  style={{
                    backgroundColor: "#FF641B",
                    pointerEvents: "auto",
                    color: "#fff",
                    border: "none",
                    padding: "12px 24px",
                    borderRadius: "8px",
                    fontSize: "1rem",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  Configure <i className="fas fa-cog"></i>
                </button>
              </div>{" "}
            </div>
          </>
        ) : (
          <>
            <div
              style={{
                position: "absolute",
                width: "80vw",
                height: "80vh",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <img
                src={DummyImageGA}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "scale-down",
                  overflow: "hidden",
                }}
                alt="Google Analytics Preview"
              />
            </div>

            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 10,
                pointerEvents: "none",
              }}
            >
              <button
                onClick={handleConnectClick}
                style={{
                  backgroundColor: "#FF641B",
                  pointerEvents: "auto",
                  color: "#fff",
                  border: "none",
                  padding: "12px 24px",
                  borderRadius: "8px",
                  fontSize: "1rem",
                  cursor: "pointer",
                }}
              >
                Connect to Google Analytics
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default GoogleAnalytics;
